import { Eye, MoreHorizontal, Plus, Radio, Settings2, Trash2 } from "lucide-react";
import * as React from "react";
import { ConfirmDialog } from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { EmptyState, ErrorState, TableSkeleton } from "@/components/ui/feedback";
import { CopyButton } from "@/components/ui/misc";
import {
  MonoCell, Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { useToast } from "@/components/ui/toast";
import { Page, PageHeader } from "@/components/layout/page";
import { GatewayFormDialog } from "@/features/gateway-form";
import { ApiError } from "@/lib/api";
import { useDeleteGateway, useGateways, useRevealGatewayPassword } from "@/lib/queries";
import type { Gateway, GatewaySecret } from "@/lib/types";
import { formatDateTime } from "@/lib/utils";

/**
 * SIP trunks — the carriers calls leave on.
 *
 * Admin and superadmin only, and not because the screen is dangerous to look at: the list
 * carries the carrier username, and the trunks it holds are what every domain's outbound calls
 * ride. Supervisors and reps are refused by the API too.
 *
 * Saving anything here publishes a profile rescan, so a trunk is live on the next call rather
 * than after a restart. The one thing the console will not do is delete a trunk a domain still
 * routes through — that would leave the domain naming a row that is gone and its reps dialing
 * into an announcement, so the API refuses and the count in the table says why.
 */
export function GatewaysPage() {
  const toast = useToast();
  const { data, isPending, error, refetch } = useGateways();

  const [formOpen, setFormOpen] = React.useState(false);
  const [editing, setEditing] = React.useState<Gateway | undefined>();
  const [deleting, setDeleting] = React.useState<Gateway | undefined>();
  const [secret, setSecret] = React.useState<GatewaySecret | undefined>();
  const remove = useDeleteGateway();
  const reveal = useRevealGatewayPassword();

  return (
    <Page>
      <PageHeader
        title="Gateways"
        description="The SIP trunks this platform's calls leave on. Each domain picks which of them its reps dial out through."
        action={
          <Button
            variant="default"
            onClick={() => {
              setEditing(undefined);
              setFormOpen(true);
            }}
          >
            <Plus />
            New gateway
          </Button>
        }
      />

      <Card className="overflow-hidden">
        {isPending ? (
          <TableSkeleton cols={6} />
        ) : error ? (
          <ErrorState
            error={error}
            title="Could not load gateways"
            action={
              <Button variant="outline" onClick={() => void refetch()}>
                Try again
              </Button>
            }
          />
        ) : data.length === 0 ? (
          <EmptyState
            icon={Radio}
            title="No gateways yet"
            description="Add the trunk your carrier issued. Until one exists, no domain can dial out."
            action={
              <Button variant="default" onClick={() => setFormOpen(true)}>
                <Plus />
                New gateway
              </Button>
            }
          />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Gateway</TableHead>
                <TableHead>Realm</TableHead>
                <TableHead>Profile</TableHead>
                <TableHead>Register</TableHead>
                <TableHead>Domains</TableHead>
                <TableHead>Added</TableHead>
                <TableHead className="w-10" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.map((g) => (
                <TableRow key={g.id}>
                  <TableCell>
                    <span className="inline-flex items-center gap-1.5">
                      <span className="font-medium text-text">{g.name}</span>
                      {!g.enabled && (
                        <Badge tone="warning" size="sm">
                          Disabled
                        </Badge>
                      )}
                    </span>
                    {g.description && (
                      <p className="text-xs text-text-muted">{g.description}</p>
                    )}
                  </TableCell>
                  <MonoCell className="text-text-muted">
                    {g.realm}
                    {/* The proxy is only worth a line when it is not simply the realm. */}
                    {g.proxy !== g.realm && (
                      <p className="text-xs text-text-subtle">via {g.proxy}</p>
                    )}
                  </MonoCell>
                  <MonoCell className="text-text-muted">{g.profile_name}</MonoCell>
                  <TableCell>
                    <Badge tone={g.register ? "neutral" : "outline"} size="sm">
                      {g.register ? "Registers" : "IP auth"}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge tone={g.domain_count > 0 ? "neutral" : "outline"}>
                      {g.domain_count}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-xs text-text-muted">
                    {formatDateTime(g.created_at)}
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon-sm" aria-label={`Actions for ${g.name}`}>
                          <MoreHorizontal />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent>
                        <DropdownMenuItem
                          onSelect={() => {
                            setEditing(g);
                            setFormOpen(true);
                          }}
                        >
                          <Settings2 />
                          Edit gateway
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onSelect={async () => {
                            try {
                              setSecret(await reveal.mutateAsync(g.id));
                            } catch (e) {
                              toast.error(
                                "Could not read the password",
                                e instanceof ApiError ? e.message : "Please try again.",
                              );
                            }
                          }}
                        >
                          <Eye />
                          Reveal password
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem destructive onSelect={() => setDeleting(g)}>
                          <Trash2 />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      <GatewayFormDialog open={formOpen} onOpenChange={setFormOpen} gateway={editing} />

      {/* Not a page or a route: a revealed credential should live exactly as long as the dialog
          showing it, and go when it closes. */}
      <Dialog open={Boolean(secret)} onOpenChange={(open) => !open && setSecret(undefined)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{secret?.name}</DialogTitle>
            <DialogDescription>
              The credentials this trunk authenticates to the carrier with.
            </DialogDescription>
          </DialogHeader>
          <DialogBody className="space-y-3">
            <div>
              <p className="text-xs font-medium text-text">Username</p>
              <p className="inline-flex items-center gap-1 font-mono text-sm">
                {secret?.username}
                <CopyButton value={secret?.username ?? ""} />
              </p>
            </div>
            <div>
              <p className="text-xs font-medium text-text">Password</p>
              <p className="inline-flex items-center gap-1 font-mono text-sm">
                {secret?.password}
                <CopyButton value={secret?.password ?? ""} />
              </p>
            </div>
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSecret(undefined)}>
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={Boolean(deleting)}
        onOpenChange={(open) => !open && setDeleting(undefined)}
        title="Delete this gateway?"
        description={
          <>
            <span className="font-medium">{deleting?.name}</span> will be removed and the profile
            re-read. Domains routing outbound calls through it block this — point them somewhere
            else first.
          </>
        }
        confirmLabel="Delete gateway"
        destructive
        loading={remove.isPending}
        onConfirm={async () => {
          if (!deleting) return;
          try {
            await remove.mutateAsync(deleting.id);
            toast.success("Gateway deleted", deleting.name);
            setDeleting(undefined);
          } catch (e) {
            toast.error("Could not delete", e instanceof ApiError ? e.message : "Please try again.");
          }
        }}
      />
    </Page>
  );
}
